import Web3 from 'web3';

import { ITransactionConfiguration } from 'models/web3Singleton/i-transaction-configuration.interface';

import { TRADE_CONTRACT_ABI } from 'constants/abi/trade-contract-abi.constant';
import { NETWORK_SETTINGS } from 'constants/network.constant';
import { WEB3 } from 'constants/web3.constant';

import { BaseWeb3Service } from './base-web3.service';

/**
 * Service for handling trade contract interactions
 * Manages buying, selling, and signature verification on the trade contract
 */
export class TradeService extends BaseWeb3Service {
  private readonly tradeAbi = TRADE_CONTRACT_ABI;
  private readonly tradeAddress = NETWORK_SETTINGS.TRADE.ADDRESS;
  private readonly tradeContract: any;

  /** Multiplier applied to the estimate so a slightly-heavier-than-estimated tx still fits. */
  private static readonly GAS_BUFFER = 1.2;
  /**
   * A trade is ~150k–400k gas. web3's `eth_estimateGas` returns a runaway value (near/above
   * the block gas cap) for a tx that WOULD revert but for which the node can't find a passing
   * gas value — sending that raw tx just gets rejected by the node ("gas limit too high"),
   * masking the real revert. Anything at/above this ceiling is treated as a doomed tx.
   */
  private static readonly MAX_PLAUSIBLE_GAS = 5_000_000;

  constructor(web3: Web3, torusProvider?: any) {
    super(web3, torusProvider);
    this.tradeContract = this.createContract(this.tradeAbi, this.tradeAddress);
  }

  /**
   * Estimates gas for a prepared contract method and returns it with a safety buffer.
   * `estimateGas` throws with the REAL revert reason when a tx would revert, and returns a
   * runaway number when the node can't pin a passing value — both are caught here (the latter
   * as an explicit throw) so the caller can surface the real failure instead of sending a
   * transaction the node will reject outright.
   */
  private async estimateGasWithGuard(method: any, from: string): Promise<number> {
    const estimated = Number(await method.estimateGas({ from }));

    if (!Number.isFinite(estimated) || estimated >= TradeService.MAX_PLAUSIBLE_GAS) {
      throw new Error(
        `Gas estimate abnormally high (${estimated}) — the transaction would likely revert.`
      );
    }

    return Math.ceil(estimated * TradeService.GAS_BUFFER);
  }

  async isSignatureUsed(signatureHash: string): Promise<boolean> {
    return await this.tradeContract.methods.isSignatureUsed(signatureHash).call();
  }

  /**
   * Creates a state hash for sale verification
   */
  async createStateHashForSale(abi: any[], assetAddress: string, tokenId: string): Promise<string> {
    const gasPrice = await this.getGasPrice();
    const buildAddress = NETWORK_SETTINGS.BUILD.ADDRESS;
    const contract = this.createContract(abi, buildAddress);

    return assetAddress === buildAddress
      ? await contract.methods.stateHash(tokenId).call({ gasPrice })
      : WEB3.nullStateHash;
  }

  /**
   * Purchases an asset using a trade offer
   */
  async purchaseAsset(configuration: ITransactionConfiguration): Promise<void> {
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    const method = this.tradeContract.methods.buyOnOffer(
      configuration.to,
      configuration.signature,
      configuration.tradeRequest
    );

    // Estimate gas explicitly (with a buffer + runaway guard) before sending — otherwise
    // web3 auto-estimates and a reverting tx yields a gas value above the block cap that the
    // node rejects, hiding the real reason.
    let gas: number;
    try {
      gas = await this.estimateGasWithGuard(method, configuration.walletAddress);
    } catch (estimateErr) {
      console.error('Purchase asset gas estimation failed:', estimateErr);
      configuration.offerCanceled(estimateErr as Error);
      return;
    }

    await method
      .send({ from: configuration.walletAddress, gas, gasPrice })
      .on('transactionHash', (hash: string) => configuration.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && configuration.offerAccepted())
      .on('error', (error: Error) => {
        console.error('Purchase asset transaction failed:', error);
        configuration.offerCanceled(error);
      });
  }

  /**
   * Accepts a bid offer on the trade contract
   */
  async acceptBidOffer(configuration: ITransactionConfiguration): Promise<void> {
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    const method = this.tradeContract.methods.sellOnBid(
      configuration.to,
      configuration.signature,
      configuration.tradeRequest
    );

    // Same explicit-estimate + guard as purchaseAsset (shared .send pattern).
    let gas: number;
    try {
      gas = await this.estimateGasWithGuard(method, configuration.walletAddress);
    } catch (estimateErr) {
      console.error('Accept bid offer gas estimation failed:', estimateErr);
      configuration.offerCanceled(estimateErr as Error);
      return;
    }

    await method
      .send({ from: configuration.walletAddress, gas, gasPrice })
      .on('transactionHash', (hash: string) => configuration.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && configuration.offerAccepted())
      .on('error', (error: Error) => {
        console.error('Accept bid offer transaction failed:', error);
        configuration.offerCanceled(error);
      });
  }
}
